import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  concatMap,
  distinctUntilChanged,
  EMPTY,
  filter,
  Observable,
  of,
  skip,
  startWith,
  switchMap,
} from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { sentryLog } from '@blaze-shared/logger-util';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { findColumn } from '@blaze-shared/schema-domain';
import { isDeepEqual } from '@blaze-shared/shared-util';

import { DataProviderQuery, InputDataPackage } from '../../io-data.models';

import { DataSourceProvider } from './data-source.data-provider';
import { createTagItemsSchema } from './utils/schema.utils';

@Injectable({ providedIn: 'root' })
export class ColumnTagsDataProvider implements DataSourceProvider {
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store);
  readonly sourceType = 'column-tags' as const;

  getDataStream({ source }: DataProviderQuery): Observable<InputDataPackage> {
    const dataSourceId = source.id;
    const columnIdOrName = source.name;
    const isWorkflow = !this.schemaService.isDataset(dataSourceId);
    const source$ = isWorkflow
      ? this.schemaService.observeWorkflowSchema(dataSourceId)
      : this.schemaService.obtainSchema(dataSourceId).pipe(
          switchMap((obtainedSchema) =>
            this.store.select(runtimeSelectors.selectSchemaById(dataSourceId)).pipe(
              // This can probably be removed in Sprint 1.11 (schema will not be cleared on navigation)
              filter((schema) => !!schema),
              // Skip the first emission, as it comes from obtainSchema
              skip(1),
              startWith(obtainedSchema),
              distinctUntilChanged(),
            ),
          ),
        );

    return source$.pipe(
      concatMap((schema) => {
        if (!schema) {
          sentryLog('Tags source schema missing', 'error');
          return EMPTY;
        }
        const column = findColumn(schema.columns, columnIdOrName);
        return of(<InputDataPackage>{
          sourceType: 'column-tags',
          data: column?.tagDefinition?.items,
          schema: column ? createTagItemsSchema(column) : schema,
          column,
        });
      }),
      distinctUntilChanged(isDeepEqual),
    );
  }
}
